import React from 'react';
import { PortableText as PortableTextComponent } from '@portabletext/react';
import { urlFor } from '../lib/sanity';

interface PortableTextProps {
  content: any[];
  className?: string;
}

const PortableText: React.FC<PortableTextProps> = ({ content, className = '' }) => {
  const components = {
    types: {
      image: ({ value }: any) => {
        return (
          <div className="my-8">
            <img
              src={urlFor(value).url()}
              alt={value.alt || 'Blog image'}
              className="w-full h-auto rounded-lg shadow-lg"
            />
            {value.caption && (
              <p className="text-sm text-gray-600 mt-2 text-center">{value.caption}</p>
            )}
          </div>
        );
      },
      code: ({ value }: any) => {
        return (
          <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto my-4">
            <code>{value.code}</code>
          </pre>
        );
      },
    },
    block: {
      h1: ({ children }: any) => (
        <h1 className="text-3xl font-bold mb-4 mt-8 text-gray-900">{children}</h1>
      ),
      h2: ({ children }: any) => (
        <h2 className="text-2xl font-bold mb-3 mt-6 text-gray-900">{children}</h2>
      ),
      h3: ({ children }: any) => (
        <h3 className="text-xl font-bold mb-2 mt-4 text-gray-900">{children}</h3>
      ),
      h4: ({ children }: any) => (
        <h4 className="text-lg font-bold mb-2 mt-4 text-gray-900">{children}</h4>
      ),
      normal: ({ children }: any) => (
        <p className="mb-4 text-gray-700 leading-relaxed">{children}</p>
      ),
      blockquote: ({ children }: any) => (
        <blockquote className="border-l-4 border-primary pl-4 italic text-gray-600 my-4">
          {children}
        </blockquote>
      ),
    },
    list: {
      bullet: ({ children }: any) => (
        <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700">{children}</ul>
      ),
      number: ({ children }: any) => (
        <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-700">{children}</ol>
      ),
    },
    listItem: ({ children }: any) => (
      <li className="text-gray-700">{children}</li>
    ),
    marks: {
      link: ({ children, value }: any) => {
        const target = (value?.href || '').startsWith('http') ? '_blank' : undefined;
        return (
          <a
            href={value?.href}
            target={target}
            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
            className="text-primary hover:text-blue-700 underline"
          >
            {children}
          </a>
        );
      },
      strong: ({ children }: any) => (
        <strong className="font-bold text-gray-900">{children}</strong>
      ),
      em: ({ children }: any) => (
        <em className="italic">{children}</em>
      ),
      code: ({ children }: any) => (
        <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">{children}</code>
      ),
    },
  };

  return (
    <div className={`prose prose-lg max-w-none ${className}`}>
      <PortableTextComponent value={content} components={components} />
    </div>
  );
};

export default PortableText; 